// src/components/LoyaltyProgramSteps/Step3RewardsRedemption.js
import React from 'react';
import { GiftIcon, ReceiptPercentIcon, PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';

const Step3RewardsRedemption = ({ formData, updateFormData, updateReward, addReward, removeReward }) => {
    const rewardTypes = [
        { value: 'discount', label: 'Discount (e.g., R50 off)' },
        { value: 'free_item', label: 'Free Item (e.g., Free Coffee)' },
        { value: 'percentage_discount', label: 'Percentage Discount (e.g., 10% off)' },
        { value: 'exclusive_access', label: 'Exclusive Access (e.g., Early sale access)' },
        { value: 'custom', label: 'Custom Reward' },
    ];

    const renderRewardsByProgramType = () => {
        switch (formData.programType) {
            case 'points':
                return (
                    <div className="space-y-4">
                        {formData.rewards.map((reward) => (
                            <div key={reward.id} className="border border-gray-200 p-4 rounded-md bg-gray-50 relative">
                                {formData.rewards.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeReward(reward.id)}
                                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                                        title="Remove Reward"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                )}
                                <h4 className="text-md font-semibold text-gray-800 mb-3">Reward {reward.id}</h4> {/* Simple ID for display */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor={`points-needed-${reward.id}`} className="block text-sm font-medium text-gray-700">Points Needed</label>
                                        <input
                                            id={`points-needed-${reward.id}`}
                                            name={`points-needed-${reward.id}`}
                                            type="number"
                                            min="1"
                                            required
                                            className="mt-1 block w-full pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="e.g., 100"
                                            value={reward.pointsNeeded}
                                            onChange={(e) => updateReward(reward.id, 'pointsNeeded', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor={`reward-type-${reward.id}`} className="block text-sm font-medium text-gray-700">Reward Type</label>
                                        <select
                                            id={`reward-type-${reward.id}`}
                                            name={`reward-type-${reward.id}`}
                                            required
                                            className="mt-1 block w-full py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white"
                                            value={reward.rewardType}
                                            onChange={(e) => updateReward(reward.id, 'rewardType', e.target.value)}
                                        >
                                            {rewardTypes.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <label htmlFor={`reward-value-${reward.id}`} className="block text-sm font-medium text-gray-700">Reward Value/Description</label>
                                    <input
                                        id={`reward-value-${reward.id}`}
                                        name={`reward-value-${reward.id}`}
                                        type="text"
                                        required
                                        className="mt-1 block w-full py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder={reward.rewardType === 'discount' ? 'e.g., 50 (for R50 off)' : reward.rewardType === 'percentage_discount' ? 'e.g., 10 (for 10% off)' : 'e.g., Free Coffee'}
                                        value={reward.value}
                                        onChange={(e) => updateReward(reward.id, 'value', e.target.value)}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {reward.rewardType === 'discount' && `Enter value (e.g., 50 for R50 off)`}
                                        {reward.rewardType === 'percentage_discount' && `Enter percentage (e.g., 10 for 10% off)`}
                                        {reward.rewardType === 'free_item' && `Enter item name (e.g., Cappuccino)`}
                                        {reward.rewardType === 'exclusive_access' && `Describe access (e.g., Early Black Friday Access)`}
                                        {reward.rewardType === 'custom' && `Describe your custom reward`}
                                    </p>
                                </div>
                                <div className="mt-4">
                                    <label htmlFor={`reward-description-${reward.id}`} className="block text-sm font-medium text-gray-700">Customer Facing Description</label>
                                    <textarea
                                        id={`reward-description-${reward.id}`}
                                        name={`reward-description-${reward.id}`}
                                        rows="2"
                                        required
                                        className="mt-1 block w-full py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="Describe this reward clearly to your customers (e.g., 'R50 off your next purchase', 'A free medium cappuccino')"
                                        value={reward.rewardDescription}
                                        onChange={(e) => updateReward(reward.id, 'rewardDescription', e.target.value)}
                                    ></textarea>
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={addReward}
                            className="flex items-center text-indigo-600 hover:text-indigo-800 text-sm font-medium mt-4"
                        >
                            <PlusCircleIcon className="h-5 w-5 mr-2" />
                            Add Another Reward
                        </button>
                    </div>
                );
            case 'tier':
                return (
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                        <p className="text-sm text-blue-700">
                            For Tier-based programs, rewards are typically associated with reaching or maintaining specific tiers. You will define the benefits for each tier in a dedicated 'Tier Management' section.
                        </p>
                        {/* Placeholder for future tier-specific reward input */}
                    </div>
                );
            case 'stamp':
                return (
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="stamp-reward-description" className="block text-sm font-medium text-gray-700">Reward for Completed Stamp Card</label>
                            <textarea
                                id="stamp-reward-description"
                                name="stamp-reward-description"
                                rows="2"
                                required
                                className="mt-1 block w-full py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                placeholder="e.g., 'Free medium coffee on your 6th purchase', 'R20 off your next order'"
                                value={formData.stampRewardDescription || ''}
                                onChange={(e) => updateFormData('stampRewardDescription', e.target.value)}
                            ></textarea>
                        </div>
                        <div>
                            <label htmlFor="stamp-reward-value" className="block text-sm font-medium text-gray-700">Reward Value (e.g., 20 for R20, 'Free Coffee')</label>
                            <input
                                id="stamp-reward-value"
                                name="stamp-reward-value"
                                type="text"
                                required
                                className="mt-1 block w-full py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                placeholder="e.g., 'Free Latte' or '20'"
                                value={formData.stampRewardValue || ''}
                                onChange={(e) => updateFormData('stampRewardValue', e.target.value)}
                            />
                        </div>
                    </div>
                );
            case 'spend':
                return (
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="spend-reward-description" className="block text-sm font-medium text-gray-700">Reward for Reaching Spend Threshold</label>
                            <textarea
                                id="spend-reward-description"
                                name="spend-reward-description"
                                rows="2"
                                required
                                className="mt-1 block w-full py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                placeholder="e.g., 'Get R50 off your next purchase', 'Unlock a special VIP discount'"
                                value={formData.spendRewardDescription || ''}
                                onChange={(e) => updateFormData('spendRewardDescription', e.target.value)}
                            ></textarea>
                        </div>
                        <div>
                            <label htmlFor="spend-reward-value" className="block text-sm font-medium text-gray-700">Reward Value (e.g., 50 for R50 off, '10% off next purchase')</label>
                            <input
                                id="spend-reward-value"
                                name="spend-reward-value"
                                type="text"
                                required
                                className="mt-1 block w-full py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                placeholder="e.g., '50' or '10% off'"
                                value={formData.spendRewardValue || ''}
                                onChange={(e) => updateFormData('spendRewardValue', e.target.value)}
                            />
                        </div>
                    </div>
                );
            default:
                return <p className="text-gray-600">Please select a Program Type in Step 1 to configure rewards.</p>;
        }
    };

    return (
        <fieldset className="border p-4 rounded-md space-y-4">
            <legend className="text-lg font-medium text-gray-900 flex items-center">
                <ReceiptPercentIcon className="h-6 w-6 text-indigo-500 mr-2" />
                Step 3: Rewards & Redemption
            </legend>

            {renderRewardsByProgramType()}

            {/* General Redemption Instructions */}
            <div className="mt-6 pt-4 border-t border-gray-200">
                <label htmlFor="redemption-instructions" className="block text-sm font-medium text-gray-700">Redemption Instructions (Optional)</label>
                <textarea
                    id="redemption-instructions"
                    name="redemption-instructions"
                    rows="3"
                    className="mt-1 block w-full py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="e.g., 'Show your QR code at checkout to redeem', 'Vouchers emailed directly', 'Rewards expire in 60 days'"
                    value={formData.redemptionInstructions}
                    onChange={(e) => updateFormData('redemptionInstructions', e.target.value)}
                ></textarea>
            </div>
        </fieldset>
    );
};

export default Step3RewardsRedemption;